import { useEffect, useMemo, useState } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from "@headlessui/react";
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  CheckIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";

import { BottomSheet } from "components/BottomSheet";
import { Notes } from "components/Notes";
import { Link } from "components/Link";
import { Loader } from "components/Loader";
import { ProxyAssignmentIcon } from "components/ProxyAssignmentIcon";
import { SeparatorIcon } from "components/icons/SeparatorIcon";
import {
  SheetPrimaryButton,
  SheetSecondaryButton,
} from "pages/app/uppdrag/transportSedel/operator/SheetActions";
import { PriorityIcon } from "../PriorityIcon";
import {
  getUppdragDateAsString,
  Uppdrag,
  UppdragStatus,
} from "types/uppdrag.type";
import { getActivityTypeName } from "enums/ActivityType.enum";
import { getSeparatorDetailedLabel } from "types/separator/separator.helpers";
import {
  ActivityItem,
  buildUppdragActivities,
} from "pages/app/uppdrag/operator/uppdragActivities";
import { getUppdrag, patchUppdrag, UppdragQueryKey } from "api/uppdrag.api";
import { UppdragPageTab } from "pages/app/uppdrag/UppdragPage";
import { useSwitchUser } from "api/auth.api";
import { useNotification } from "hooks/useNotification";
import { useUsers } from "api/user.api";
import { useCurrentCompany } from "api/company.api";
import userStore, { getUser } from "store/user.store";
import { Role } from "types/enums";
import { User } from "types/user.type";
import { cn } from "helpers/classNames";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  uppdrag: Uppdrag;
  // When provided, runs instead of the default close + navigate after a
  // successful start (e.g. when already on the uppdrag page, just refetch).
  onSuccess?: () => void;
};

const schema = z.object({
  assignedToUserUuid: z
    .string({ required_error: "Välj en operatör" })
    .uuid("Välj en operatör"),
});

type FormValues = z.infer<typeof schema>;

const PIN_LENGTH = 4;

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

const getSwitchErrorMessage = (error: unknown) => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  switch (status) {
    case 401:
      return "Fel PIN-kod. Efter tre felaktiga försök behöver du skapa en ny.";
    case 403:
      return "Du kan inte byta till den här användaren.";
    case 404:
      return "Användaren hittades inte.";
    default:
      return "Något gick fel. Försök igen.";
  }
};

export const UppdragPreviewModal = ({
  isOpen,
  onClose,
  uppdrag,
  onSuccess,
}: Props) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const notification = useNotification();

  const currentUser = userStore(getUser);
  const { data: currentCompany } = useCurrentCompany();
  const { data: users, isLoading: isLoadingUsers } = useUsers(
    {},
    { roles: [Role.Operator, Role.Admin] },
  );

  // The list-level uppdrag lacks the nested activity data (inspections,
  // tömningar). Fetch the full uppdrag — same endpoint as UppdragPage — while
  // the sheet is open and render its details once loaded.
  const {
    data: detailedUppdrag,
    isLoading: isLoadingUppdrag,
    isError: isUppdragError,
    isFetching: isFetchingUppdrag,
    refetch: refetchUppdrag,
  } = useQuery({
    ...getUppdrag(uppdrag.uuid),
    enabled: isOpen,
  });
  // Only the fully-fetched uppdrag is safe to act on; the partial list item
  // must never stand in for a failed fetch (it would let the operator start an
  // uppdrag against incomplete data).
  const detail = detailedUppdrag ?? uppdrag;
  const isDetailReady = !!detailedUppdrag;

  // Surface fetch failures with a toast. `notification` is recreated on every
  // render, so depend on the error flag alone to fire once per failure.
  useEffect(() => {
    if (isUppdragError) {
      notification.error("Det gick inte att hämta uppdraget. Försök igen.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUppdragError]);

  // Preselect the already-assigned operator; for Norva, fall back to the
  // current user so the picker starts on oneself.
  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      assignedToUserUuid:
        uppdrag.assignedToUser?.uuid ??
        (currentCompany?.isNorva ? currentUser?.uuid : undefined),
    },
  });

  const [step, setStep] = useState<"details" | "pin">("details");
  const [pendingOperator, setPendingOperator] = useState<User | null>(null);
  const [pinCode, setPinCode] = useState("");
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinFocused, setPinFocused] = useState(false);

  const operators = useMemo(() => {
    const includeAdmins =
      currentCompany?.settings.isUppdragSchedulingToAdminActivated ?? false;
    return (users ?? [])
      .filter((u) => u.role === Role.Operator || includeAdmins)
      .sort((a, b) => (a.name < b.name ? -1 : 1));
  }, [users, currentCompany]);

  const switchUserMutation = useSwitchUser();

  const startUppdragMutation = useMutation({
    ...patchUppdrag(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
      queryClient.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });

      // Caller opts out of the default navigation (e.g. already on the page).
      if (onSuccess) {
        onSuccess();
        return;
      }

      onClose();

      // Jump straight to the uppgift the operator will work on: a tömning is
      // handled on the transport-sedel tab; otherwise open the first
      // inspection/protocol directly. Multiple uppgifter → the first one wins.
      if (detail.wasteList.length > 0) {
        navigate({
          to: "/app/uppdrag/$uuid",
          params: { uuid: uppdrag.uuid },
          search: { tab: UppdragPageTab.TransportSedel },
        });
        return;
      }

      const sixMonths = detail.sixMonthsInspectionList[0];
      if (sixMonths) {
        navigate({
          to: "/app/uppdrag/$uuid/sixm/$sixmid",
          params: { uuid: uppdrag.uuid, sixmid: sixMonths.id.toString() },
        });
        return;
      }

      const fiveYears = detail.fiveYearsInspectionList[0];
      if (fiveYears) {
        navigate({
          to: "/app/uppdrag/$uuid/fivey/$fiveyid",
          params: { uuid: uppdrag.uuid, fiveyid: fiveYears.id.toString() },
        });
        return;
      }

      const waterMeasurement = detail.waterMeasurementProtocolList[0];
      if (waterMeasurement) {
        navigate({
          to: "/app/uppdrag/$uuid/provtagning/$pid",
          params: { uuid: uppdrag.uuid, pid: waterMeasurement.id.toString() },
        });
        return;
      }

      navigate({
        to: "/app/uppdrag/$uuid",
        params: { uuid: uppdrag.uuid },
      });
    },
    onError: () => notification.error("Det gick inte att påbörja uppdraget"),
  });

  const startUppdrag = (assignedToUserUuid: string) =>
    startUppdragMutation.mutate({
      id: uppdrag.id,
      payload: {
        assignedToUserUuid,
        status: UppdragStatus.Started,
      },
    });

  const onSubmit = handleSubmit((data) => {
    // Block start until the full uppdrag has loaded — partial list data is not
    // enough to start against.
    if (!isDetailReady) return;

    const selected = operators.find((o) => o.uuid === data.assignedToUserUuid);
    if (!selected) {
      throw Error("No users in UppdragPreviewModal");
    }

    // Assigning to oneself → start directly. Otherwise the device must be handed
    // over: confirm with the new operator's PIN and switch account before start.
    if (currentUser && selected.uuid === currentUser.uuid) {
      startUppdrag(selected.uuid);
      return;
    }

    setPendingOperator(selected);
    setPinCode("");
    setPinError(null);
    setStep("pin");
  });

  const onConfirmSwitch = async () => {
    if (!pendingOperator) return;
    // Guard against a second confirm while the switch/start is in flight.
    if (switchUserMutation.isPending || startUppdragMutation.isPending) return;
    if (pinCode.length < PIN_LENGTH) {
      setPinError("Ange en giltig PIN-kod");
      return;
    }

    setPinError(null);
    try {
      await switchUserMutation.mutateAsync({
        userId: pendingOperator.id,
        // Raw string — keep leading zeros, no numeric coercion.
        pinCode,
      });
      // Session is now the new operator — start the uppdrag as them.
      startUppdrag(pendingOperator.uuid);
    } catch (error) {
      setPinError(getSwitchErrorMessage(error));
    }
  };

  const handleAfterClose = () => {
    reset();
    setStep("details");
    setPendingOperator(null);
    setPinCode("");
    setPinError(null);
    switchUserMutation.reset();
  };

  const addressLine = [
    detail.facility.address,
    [detail.facility.postalCode, detail.facility.city]
      .filter(Boolean)
      .join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  const isPin = step === "pin";
  const isSwitching =
    switchUserMutation.isPending || startUppdragMutation.isPending;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      afterClose={handleAfterClose}
      disableOutsideClose
      title={isPin ? "Byt operatör" : `Uppdrag #${uppdrag.serialNumber}`}
      subtitle={
        isPin
          ? "Bekräfta med PIN-kod"
          : !currentCompany?.isNorva
            ? "Granska och påbörja uppdrag"
            : "Granska och tilldela en operatör"
      }
      footer={
        isPin ? (
          <>
            <SheetSecondaryButton onClick={() => setStep("details")}>
              Tillbaka
            </SheetSecondaryButton>
            <SheetPrimaryButton
              type="button"
              onClick={onConfirmSwitch}
              isLoading={isSwitching}
              loadingText="Byter..."
            >
              Byt och påbörja
            </SheetPrimaryButton>
          </>
        ) : (
          <>
            <SheetSecondaryButton onClick={onClose}>Stäng</SheetSecondaryButton>
            <SheetPrimaryButton
              type="button"
              onClick={onSubmit}
              isLoading={startUppdragMutation.isPending}
              loadingText="Startar..."
              disabled={!isDetailReady}
            >
              Påbörja
            </SheetPrimaryButton>
          </>
        )
      }
    >
      {isPin ? (
        <div className="space-y-5">
          {/* Account switch warning */}
          <div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <ExclamationTriangleIcon className="h-5 w-5 flex-none text-amber-600" />
            <div className="text-sm leading-relaxed">
              <p className="font-semibold text-amber-900">Kontobyte</p>
              <p className="mt-1 text-amber-800">
                Du byter nu till {pendingOperator?.name}. Ange din PIN-kod för
                att bekräfta.
              </p>
            </div>
          </div>

          {/* New operator */}
          <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4">
            <span className="grid h-11 w-11 flex-none place-items-center rounded-full bg-blue-600 text-sm font-bold text-white">
              {pendingOperator ? getInitials(pendingOperator.name) : ""}
            </span>
            <div className="min-w-0">
              <p className="truncate text-base font-semibold text-gray-900">
                {pendingOperator?.name}
              </p>
              <p className="text-sm text-gray-500">Ny operatör</p>
            </div>
          </div>

          {/* PIN input — four distinct digit cells */}
          <div>
            <label
              htmlFor="operator-pin"
              className="mb-2 block text-base font-semibold text-gray-900"
            >
              PIN-kod
            </label>

            {/* A single transparent input drives the value; the cells below are
                the visible UI. Tapping anywhere focuses the input. */}
            <div className="relative">
              <input
                id="operator-pin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                maxLength={PIN_LENGTH}
                value={pinCode}
                onChange={(e) => {
                  setPinCode(e.target.value.replace(/\D/g, ""));
                  setPinError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  if (
                    switchUserMutation.isPending ||
                    startUppdragMutation.isPending
                  )
                    return;
                  onConfirmSwitch();
                }}
                onFocus={() => setPinFocused(true)}
                onBlur={() => setPinFocused(false)}
                aria-label={`PIN-kod, ${PIN_LENGTH} siffror`}
                className="absolute inset-0 z-10 h-full w-full cursor-pointer text-transparent caret-transparent opacity-0 outline-none"
              />
              <div className="flex gap-2.5" aria-hidden="true">
                {Array.from({ length: PIN_LENGTH }).map((_, i) => {
                  const filled = i < pinCode.length;
                  const isActive =
                    pinFocused &&
                    (i === pinCode.length ||
                      (pinCode.length === PIN_LENGTH && i === PIN_LENGTH - 1));
                  return (
                    <div
                      key={i}
                      className={cn(
                        "flex h-16 flex-1 items-center justify-center rounded-2xl border-2 bg-white transition",
                        pinError
                          ? "border-red-300"
                          : isActive
                            ? "border-blue-500 ring-4 ring-blue-500/15"
                            : filled
                              ? "border-gray-300"
                              : "border-gray-200",
                      )}
                    >
                      {filled ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-900" />
                      ) : isActive ? (
                        <span className="h-7 w-0.5 animate-pulse rounded-full bg-blue-500" />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {pinError && (
              <p className="mt-2 flex items-center gap-1 text-sm text-red-600">
                <ExclamationCircleIcon className="h-4 w-4" />
                {pinError}
              </p>
            )}
          </div>
        </div>
      ) : isLoadingUppdrag ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : isUppdragError ? (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-red-50 text-red-500">
            <ExclamationTriangleIcon className="h-7 w-7" />
          </span>
          <div>
            <p className="text-base font-semibold text-gray-900">
              Det gick inte att hämta uppdraget
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Kontrollera din anslutning och försök igen.
            </p>
          </div>
          <button
            type="button"
            onClick={() => refetchUppdrag()}
            disabled={isFetchingUppdrag}
            className="flex h-11 items-center justify-center rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition-colors active:bg-blue-700 disabled:bg-gray-400"
          >
            {isFetchingUppdrag ? "Försöker igen..." : "Försök igen"}
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Customer / facility — highlighted card */}
          <Link
            to="/app/kunder/$customerId/facility/$facilityId"
            params={{
              customerId: detail.facility.customer.id.toString(),
              facilityId: detail.facility.id.toString(),
            }}
            className="group flex items-center gap-4 rounded-2xl border border-blue-100 bg-blue-50/50 p-4 transition hover:border-blue-200 hover:bg-blue-50"
          >
            <span className="grid h-14 w-14 flex-none place-items-center rounded-2xl bg-white text-gray-700 ring-1 ring-gray-100">
              <MapPinIcon className="h-7 w-7" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-lg font-bold tracking-tight text-gray-900">
                {detail.facility.name}
              </p>
              <p className="truncate text-sm font-medium text-gray-700">
                {detail.facility.customer.name}
              </p>
              {!!addressLine && (
                <p className="truncate text-sm text-gray-500">{addressLine}</p>
              )}
              {!!detail.parentCustomer && (
                <p className="mt-0.5 flex items-center gap-x-1 truncate text-sm font-medium text-blue-700">
                  <ProxyAssignmentIcon className="h-[14px] w-[14px] shrink-0" />
                  {detail.parentCustomer.name}
                </p>
              )}
            </div>
            <ArrowRightIcon className="h-5 w-5 flex-none text-blue-500 transition group-hover:translate-x-0.5" />
          </Link>

          {/* Schedule */}
          {!currentCompany?.isNorva && (
            <InfoTile
              icon={<CalendarDaysIcon className="h-4 w-4" />}
              label="Schemalagt"
            >
              <span className="flex items-center gap-1">
                {detail.isPrioritized && <PriorityIcon className="h-4 w-4" />}
                {getUppdragDateAsString(detail)}
              </span>
            </InfoTile>
          )}

          {/* Tasks grouped per object (separator) */}
          <PreviewActivityList uppdrag={detail} />

          {/* Description */}
          {!!detail.description && (
            <div className="rounded-2xl bg-gray-25 p-4">
              <p className="mb-1 text-sm font-semibold text-gray-900">
                Uppdragsbeskrivning
              </p>
              <p className="whitespace-pre-line text-sm leading-relaxed text-gray-600">
                {detail.description}
              </p>
            </div>
          )}

          {/* Notes */}
          {!!detail.facility.notes && (
            <div className="rounded-2xl bg-gray-25 p-4">
              <p className="mb-1 text-sm font-semibold text-gray-900">
                Anteckningar
              </p>
              <Notes
                text={detail.facility.notes}
                className="text-sm leading-relaxed text-gray-600"
              />
            </div>
          )}

          {/* Operator picker */}
          {currentCompany?.isNorva && !detail.assignedToUser && (
            <Controller
              control={control}
              name="assignedToUserUuid"
              render={({ field, fieldState }) => {
                const selected = operators.find((o) => o.uuid === field.value);
                return (
                  <div className="pt-1">
                    <label className="mb-2 block text-base font-semibold text-gray-900">
                      Operatör
                    </label>
                    <Listbox
                      value={field.value ?? null}
                      onChange={field.onChange}
                    >
                      <div className="relative">
                        <ListboxButton
                          className={cn(
                            "flex h-14 w-full items-center gap-3 rounded-xl border bg-white px-3 text-left transition focus:outline-none focus:ring-4 focus:ring-blue-500/15",
                            fieldState.error
                              ? "border-red-300 focus:border-red-400"
                              : "border-gray-200 hover:border-gray-300 focus:border-blue-500",
                          )}
                        >
                          {selected && (
                            <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-blue-600 text-xs font-bold text-white">
                              {getInitials(selected.name)}
                            </span>
                          )}
                          <span
                            className={cn(
                              "min-w-0 flex-1 truncate text-base",
                              selected
                                ? "font-medium text-gray-900"
                                : "text-gray-400",
                            )}
                          >
                            {selected?.name ?? "Ange en operatör"}
                          </span>
                          <ChevronDownIcon className="h-5 w-5 flex-none text-gray-400" />
                        </ListboxButton>

                        <ListboxOptions
                          anchor="bottom start"
                          className="z-[100] max-h-56 w-[var(--button-width)] overflow-auto rounded-2xl border border-gray-100 bg-white p-1.5 shadow-xl shadow-gray-900/10 [--anchor-gap:0.5rem] focus:outline-none"
                        >
                          {operators.length === 0 && (
                            <div className="px-3 py-2.5 text-sm text-gray-400">
                              {isLoadingUsers
                                ? "Hämtar operatörer..."
                                : "Inga operatörer tillgängliga"}
                            </div>
                          )}
                          {operators.map((operator) => (
                            <ListboxOption
                              key={operator.uuid}
                              value={operator.uuid}
                              className="group flex cursor-pointer items-center gap-3 rounded-xl px-2.5 py-2.5 transition data-[focus]:bg-blue-50"
                            >
                              <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-gray-50 text-xs font-bold text-gray-500 transition group-data-[selected]:bg-blue-600 group-data-[selected]:text-white">
                                {getInitials(operator.name)}
                              </span>
                              <span className="min-w-0 flex-1 truncate text-base font-medium text-gray-800">
                                {operator.name}
                              </span>
                              <CheckIcon className="h-5 w-5 flex-none text-blue-600 opacity-0 group-data-[selected]:opacity-100" />
                            </ListboxOption>
                          ))}
                        </ListboxOptions>
                      </div>
                    </Listbox>
                    {fieldState.error && (
                      <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600">
                        <ExclamationCircleIcon className="h-4 w-4" />
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );
              }}
            />
          )}
        </div>
      )}
    </BottomSheet>
  );
};

/* ── Compact labelled stat ── */
const InfoTile = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-4">
    <div className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-gray-400">
      {icon}
      {label}
    </div>
    <div className="flex items-center text-base font-semibold text-gray-900">
      {children}
    </div>
  </div>
);

/* ── Read-only activity list, grouped per object (separator) ── */
const PreviewActivityList = ({ uppdrag }: { uppdrag: Uppdrag }) => {
  const { items, itemsWithoutSeparator, separatorGroups } =
    buildUppdragActivities(uppdrag);

  return (
    <section>
      <h4 className="mb-2 text-sm font-medium text-gray-400">Uppgifter</h4>
      {items.length === 0 ? (
        <p className="rounded-2xl bg-gray-25 p-4 text-sm text-gray-400">
          Inga uppgifter
        </p>
      ) : (
        <div className="flex flex-col gap-y-3">
          {itemsWithoutSeparator.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
              <ul className="divide-y divide-gray-75">
                {itemsWithoutSeparator.map((item) => (
                  <PreviewActivityRow key={item.key} item={item} />
                ))}
              </ul>
            </div>
          )}
          {separatorGroups.map(({ separator, items: groupItems }) => (
            <div
              key={separator.id}
              className="overflow-hidden rounded-2xl border border-gray-100 bg-white"
            >
              <div className="border-b border-gray-75 bg-gray-25 px-4 py-2.5">
                <div className="flex items-center gap-x-1.5 text-sm font-semibold text-gray-800">
                  <SeparatorIcon className="shrink-0" />
                  <span className="truncate">
                    {getSeparatorDetailedLabel(separator)}
                  </span>
                </div>
                {!!separator.notes && (
                  <div className="mt-1.5">
                    <Notes text={separator.notes} />
                  </div>
                )}
              </div>
              <ul className="divide-y divide-gray-75">
                {groupItems.map((item) => (
                  <PreviewActivityRow key={item.key} item={item} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const PreviewActivityRow = ({ item }: { item: ActivityItem }) => (
  <li className="flex min-h-[3rem] w-full items-center gap-x-3 px-4 py-2.5">
    <span
      className="h-6 w-6 shrink-0 rounded-full border-2 border-dashed border-gray-200 bg-white"
      aria-hidden="true"
    />
    <span className="flex-1 text-sm font-semibold text-gray-900">
      {getActivityTypeName(item.type)}
    </span>
  </li>
);
